import React, { createContext, useContext, useEffect, useState, ReactNode, FC } from 'react';
import { supabase } from '../utils/supabaseClient';
import { Session, User } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSubscribed: boolean;
  credits: number;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshCredits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  isSubscribed: false,
  credits: 0,
  signOut: async () => {},
  refreshSession: async () => {},
  refreshCredits: async () => {},
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [credits, setCredits] = useState(0);

  // Fetch the current auth session and user
  const refreshSession = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    setSession(session);
    setUser(session?.user ?? null);
  };

  // Fetch user profile (subscription status & credits)
  const refreshCredits = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_subscribed, credits')
        .eq('id', user.id)
        .single();
      if (!error && data) {
        setIsSubscribed(data.is_subscribed);
        setCredits(data.credits);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  useEffect(() => {
    // On mount: load session & user
    refreshSession().then(() => setLoading(false));

    // Subscribe to auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          // signed in → get latest profile
          await refreshCredits();
        } else {
          // signed out → reset profile state
          setIsSubscribed(false);
          setCredits(0);
        }
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Fetch subscription status on initial session load
  useEffect(() => {
    if (user) {
      refreshCredits();
    }
  }, [user]);

  // Wrap signOut to clear context state immediately
  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsSubscribed(false);
    setCredits(0);
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isSubscribed,
        credits,
        signOut,
        refreshSession,
        refreshCredits,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);